import type { FC } from "react";

import { uniq } from "lodash";

import { Button, classes, Text } from "@alpacahq/ui/old";

import type { AlgoRoutingModel } from "~/v2/api/rest/elite";
import { COMMISSION_MODELS } from "./constant";

type CommissionModelTableProps = {
  currentModel?: AlgoRoutingModel;
  onSelectCommissionModel: (model: AlgoRoutingModel) => void;
};

export const CommissionModelTable: FC<CommissionModelTableProps> = ({
  currentModel,
  onSelectCommissionModel,
}) => {
  const numberSharesTraded = Object.keys(COMMISSION_MODELS);
  const fixed =
    uniq(Object.values(COMMISSION_MODELS).map(({ fixed }) => fixed)).length ===
    1
      ? [COMMISSION_MODELS[Object.keys(COMMISSION_MODELS)[0]].fixed]
      : Object.values(COMMISSION_MODELS).map(({ fixed }) => fixed);

  const onClick = (model: AlgoRoutingModel) => () =>
    onSelectCommissionModel(model);

  return (
    <div className="w-full">
      <div className="grid grid-cols-3 rounded-t bg-gray-200 p-4">
        <Text className="font-bold text-lg">Monthly Shares Traded*</Text>
        <Text className="text-center font-bold text-lg">All-in (Fixed)</Text>
        <Text className="text-center font-bold text-lg">
          Cost Plus (Tiered)
        </Text>
      </div>
      <div className="grid grid-cols-3 border-gray-200 border-x border-b">
        <div>
          {numberSharesTraded.map((sharesTraded, index) => (
            <Text
              key={sharesTraded}
              className={classes(
                "whitespace-nowrap p-4",
                index > 0 && "border-gray-200 border-t",
              )}
            >
              {sharesTraded}
            </Text>
          ))}
        </div>
        <div
          className={classes(
            fixed.length === 1 &&
              "flex flex-col items-center justify-center bg-gray-100",
          )}
        >
          {fixed.length === 1
            ? fixed.map((fixedRate) => (
                <Text
                  key={`fixed-${fixedRate}`}
                  className="whitespace-nowrap p-4 text-center"
                >
                  {fixedRate}
                </Text>
              ))
            : Object.entries(COMMISSION_MODELS).map(
                ([sharesTraded, { fixed: fixedRate }], index) => (
                  <Text
                    key={sharesTraded}
                    className={classes(
                      "whitespace-nowrap p-4 text-center",
                      index > 0 && "border-gray-200 border-t",
                    )}
                  >
                    {fixedRate}
                  </Text>
                ),
              )}
        </div>
        <div>
          {Object.entries(COMMISSION_MODELS).map(
            ([sharesTraded, { tiered: tieredRate }], index) => (
              <Text
                key={sharesTraded}
                className={classes(
                  "whitespace-nowrap p-4 text-center",
                  index > 0 && "border-gray-200 border-t",
                )}
              >
                {tieredRate}
              </Text>
            ),
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 rounded-b border border-gray-200 border-t-2">
        <div className="flex items-center p-4">
          <Text className="font-bold text-lg">Pass-Through Fees</Text>
        </div>
        <div className="flex items-center justify-center bg-gray-100 p-4">
          <Text className="text-center">Regulatory Fees</Text>
        </div>
        <div className="flex items-center justify-center p-4">
          <Text className="text-center">
            Regulatory Fees
            <br />
            Exchange Fees or Rebates
          </Text>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4 pt-2">
        <div></div>
        <Button
          onClick={onClick("all_in")}
          disabled={currentModel === "all_in"}
        >
          {currentModel === "all_in" ? "Enabled" : "Select"}
        </Button>
        <Button
          onClick={onClick("cost_plus")}
          disabled={currentModel === "cost_plus"}
        >
          {currentModel === "cost_plus" ? "Enabled" : "Select"}
        </Button>
      </div>
      <Text className="pt-4">
        *The total number of shares that have been traded within a given month
        and will be reset at the beginning of the following month.
      </Text>
      <Text className="pt-4">
        <span className="block underline">All-in Plan:</span>A straightforward,
        predictable cost structure at $0.0040 per share, encompassing all
        potential fees and rebates (not including regulatory fees).
      </Text>
      <Text className="pt-4">
        <span className="block underline">Cost Plus Plan:</span>
        Exchange fees or rebates are directly passed back to you. Alpaca will
        charge $0.0025 or lower with volume tiering as a commission on top of
        your pass-through fees or rebates. Depending on your order flows, you
        may receive rebates that can offset the Alpaca commission to lower your
        overall trading cost.
      </Text>
    </div>
  );
};
